/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import DashboardRangeTabs from "@/components/dashboard/dashboard-range-tabs";
import { fetchRevenueReports } from "@/redux/slice/dashboard/dashboard-slice";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/lib/utils";
import useAuth from "@/hooks/useAuth";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const YEAR_COLORS = ["#3B82F6", "#FBBF24", "#10B981", "#A855F7"];

const StatCard = ({ title, value, color, loading, currency }) => (
  <div className="bg-[#1A1A1A] border border-white/5 p-5 rounded-lg flex-1 min-w-[180px]">
    <p className="text-gray-400 text-sm mb-2">{title}</p>
    {loading ? (
      <Skeleton className="h-8 w-28" />
    ) : (
      <p className="text-2xl font-semibold" style={{ color }}>
        {formatCurrency(value || 0, currency)}
      </p>
    )}
  </div>
);

const CustomTooltip = ({ active, payload, label, currency = "USD" }) => {
  if (active && payload && payload.length) {
    const value = Number(payload[0]?.value || 0);

    return (
      <div className="bg-[#2A2A2A] border border-white/10 p-3 rounded shadow-xl text-center">
        <p className="text-white font-medium">
          {formatCurrency(value, currency)}
        </p>
        <p className="text-gray-400 text-xs">{label}</p>
      </div>
    );
  }
  return null;
};

const ChartSkeleton = ({ height }) => (
  <div className="w-full" style={{ height }}>
    <Skeleton className="h-full w-full" />
  </div>
);

function RevenueReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1],
    [currentYear],
  );

  const recentMonths = useMemo(
    () =>
      [2, 1, 0].map((offset) => {
        const date = new Date(currentYear, currentMonth - 1 - offset, 1);
        return {
          value: date.getMonth() + 1,
          label: MONTH_NAMES[date.getMonth()],
        };
      }),
    [currentMonth, currentYear],
  );

  const [comparisonRange, setComparisonRange] = useState("weekly");
  const [comparisonYear, setComparisonYear] = useState(currentYear);
  const [comparisonMonth, setComparisonMonth] = useState(currentMonth);

  const [totalRevenueRange, setTotalRevenueRange] = useState("monthly");
  const [totalYear, setTotalYear] = useState(currentYear);
  const [totalMonth, setTotalMonth] = useState(currentMonth);

  const [compareYear1, setCompareYear1] = useState(currentYear - 1);
  const [compareYear2, setCompareYear2] = useState(currentYear);

  const hasLoadedInitialRef = useRef(false);

  const reportData = useSelector((state) => state.dashboard?.revenueReports);
  const revenueLoading = useSelector(
    (state) => state.dashboard?.revenueLoading,
  );

  const restaurantId = user?.restaurant_id;

  const comparisonData = useMemo(
    () => reportData?.charts?.comparison?.[comparisonRange] || [],
    [reportData, comparisonRange],
  );

  const totalRevenueData = useMemo(
    () => reportData?.charts?.totalRevenue?.[totalRevenueRange] || [],
    [reportData, totalRevenueRange],
  );

  const yearlyComparisonData = useMemo(
    () => reportData?.charts?.yearlyComparison?.yearly || [],
    [reportData],
  );

  const totalRevenueFromFilteredData = useMemo(
    () =>
      totalRevenueData.reduce(
        (acc, item) => acc + Number(item?.revenue || 0),
        0,
      ),
    [totalRevenueData],
  );

  const yearlyKeys = useMemo(() => {
    const firstRow = yearlyComparisonData?.[0] || {};
    return Object.keys(firstRow)
      .filter((key) => key !== "label")
      .sort((a, b) => Number(a.replace("v", "")) - Number(b.replace("v", "")));
  }, [yearlyComparisonData]);

  const isInitialLoading = Boolean(revenueLoading?.initial);
  const isComparisonLoading =
    isInitialLoading || Boolean(revenueLoading?.comparison);
  const isTotalLoading =
    isInitialLoading || Boolean(revenueLoading?.totalRevenue);
  const isYearlyLoading =
    isInitialLoading || Boolean(revenueLoading?.yearlyComparison);

  const requestComparisonData = (
    period,
    year = comparisonYear,
    month = comparisonMonth,
  ) => {
    const params =
      period === "monthly"
        ? { period: "monthly", year, month }
        : { period: "weekly" };

    dispatch(
      fetchRevenueReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "comparison" },
      }),
    );
  };

  const requestTotalRevenueData = (
    period,
    year = totalYear,
    month = totalMonth,
  ) => {
    const params =
      period === "yearly"
        ? { period: "yearly", year }
        : { period: "monthly", year, month };

    dispatch(
      fetchRevenueReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "totalRevenue" },
      }),
    );
  };

  const requestYearlyComparisonData = (
    year1 = compareYear1,
    year2 = compareYear2,
  ) => {
    dispatch(
      fetchRevenueReports({
        restaurant_id: restaurantId,
        params: {
          period: "yearly",
          compare_year1: year1,
          compare_year2: year2,
        },
        requestMeta: { chart: "yearlyComparison" },
      }),
    );
  };

  useEffect(() => {
    if (!restaurantId || hasLoadedInitialRef.current) return;

    dispatch(
      fetchRevenueReports({
        restaurant_id: restaurantId,
        params: { period: "weekly" },
        requestMeta: { mode: "initial" },
      }),
    );

    hasLoadedInitialRef.current = true;
  }, [dispatch, restaurantId]);

  const handleComparisonRangeChange = (value) => {
    setComparisonRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "weekly") {
      requestComparisonData("weekly");
      return;
    }

    requestComparisonData("monthly", comparisonYear, comparisonMonth);
  };

  const handleComparisonYearChange = (year) => {
    setComparisonYear(year);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", year, comparisonMonth);
  };

  const handleComparisonMonthChange = (month) => {
    setComparisonMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      comparisonRange !== "monthly"
    )
      return;

    requestComparisonData("monthly", comparisonYear, month);
  };

  const handleTotalRangeChange = (value) => {
    setTotalRevenueRange(value);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (value === "yearly") {
      requestTotalRevenueData("yearly", totalYear);
      return;
    }

    requestTotalRevenueData("monthly", totalYear, totalMonth);
  };

  const handleTotalYearChange = (year) => {
    setTotalYear(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    if (totalRevenueRange === "yearly") {
      requestTotalRevenueData("yearly", year);
      return;
    }

    requestTotalRevenueData("monthly", year, totalMonth);
  };

  const handleTotalMonthChange = (month) => {
    setTotalMonth(month);

    if (
      !hasLoadedInitialRef.current ||
      !restaurantId ||
      totalRevenueRange !== "monthly"
    )
      return;

    requestTotalRevenueData("monthly", totalYear, month);
  };

  const handleCompareYear1Change = (year) => {
    setCompareYear1(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    requestYearlyComparisonData(year, compareYear2);
  };

  const handleCompareYear2Change = (year) => {
    setCompareYear2(year);

    if (!hasLoadedInitialRef.current || !restaurantId) return;

    requestYearlyComparisonData(compareYear1, year);
  };

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Revenue Reports" }}
      paths={[
        { name: "Overview", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Revenue", path: "/dashboard/Revenue-reports" },
      ]}
    >
      <div className="container mx-auto p-6 space-y-6 min-h-screen text-white ">
        <section className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Revenue</h2>
            <div className="flex gap-3">
              {comparisonRange === "monthly" && (
                <>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                      value={comparisonMonth}
                      onChange={(e) =>
                        handleComparisonMonthChange(Number(e.target.value))
                      }
                    >
                      {recentMonths.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                      value={comparisonYear}
                      onChange={(e) =>
                        handleComparisonYearChange(Number(e.target.value))
                      }
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </>
              )}
              <DashboardRangeTabs
                options={[
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                ]}
                value={comparisonRange}
                onChange={handleComparisonRangeChange}
                shape="full"
                className="border-white/10"
                buttonClassName="px-4 md:px-5 py-1.5 text-xs"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <StatCard
              title="Pick up"
              value={reportData?.stats?.pickup || 0}
              color="#FBBF24"
              loading={isInitialLoading && !reportData}
              currency={user?.currency || "USD"}
            />
            <StatCard
              title="Table"
              value={reportData?.stats?.table || 0}
              color="#EF4444"
              loading={isInitialLoading && !reportData}
              currency={user?.currency || "USD"}
            />
            <StatCard
              title="In-House"
              value={reportData?.stats?.inHouse || 0}
              color="#10B981"
              loading={isInitialLoading && !reportData}
              currency={user?.currency || "USD"}
            />
            <StatCard
              title="Delivery"
              value={reportData?.stats?.delivery || 0}
              color="#3B82F6"
              loading={isInitialLoading && !reportData}
              currency={user?.currency || "USD"}
            />
            <StatCard
              title="Online"
              value={reportData?.stats?.online || 0}
              color="#A855F7"
              loading={isInitialLoading && !reportData}
              currency={user?.currency || "USD"}
            />
          </div>
        </section>

        <section className="bg-[#1A1A1A]/40 border border-white/5 rounded-xl p-6">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-medium">
              {comparisonRange === "weekly" ? "Weekly Comparison" : "Monthly Comparison"}
            </h3>
            <div className="flex gap-4 text-xs">
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#FBBF24]" /> Pick up
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#EF4444]" /> Table
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" /> In-House
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#3B82F6]" /> Delivery
              </span>
              <span className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#A855F7]" /> Online
              </span>
            </div>
          </div>
          <div className="h-[400px] w-full">
            {isComparisonLoading ? (
              <ChartSkeleton height={400} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={comparisonData}>
                  <defs>
                    <linearGradient id="fillPickup" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="#FBBF24"
                        stopOpacity={0.15}
                      />
                      <stop offset="95%" stopColor="#FBBF24" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fillTable" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="#EF4444"
                        stopOpacity={0.15}
                      />
                      <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="fillInHouse"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#10B981"
                        stopOpacity={0.15}
                      />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient
                      id="fillDelivery"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#3B82F6"
                        stopOpacity={0.15}
                      />
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="fillOnline" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="#A855F7"
                        stopOpacity={0.15}
                      />
                      <stop offset="95%" stopColor="#A855F7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="#333"
                    strokeDasharray="0"
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  />
                  <Tooltip
                    content={
                      <CustomTooltip currency={user?.currency || "USD"} />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="pickup"
                    stroke="#FBBF24"
                    strokeWidth={2}
                    fill="url(#fillPickup)"
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="table"
                    stroke="#EF4444"
                    strokeWidth={2}
                    fill="url(#fillTable)"
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="inHouse"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#fillInHouse)"
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="delivery"
                    stroke="#3B82F6"
                    strokeWidth={2}
                    fill="url(#fillDelivery)"
                    dot={false}
                  />
                  <Area
                    type="monotone"
                    dataKey="online"
                    stroke="#A855F7"
                    strokeWidth={2}
                    fill="url(#fillOnline)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="bg-[#1A1A1A]/40 border border-white/5 rounded-xl p-6 relative">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-gray-400 text-sm mb-1">Total Revenue</h3>
              {isTotalLoading ? (
                <Skeleton className="h-10 w-36" />
              ) : (
                <p className="text-3xl font-bold text-[#FBBF24]">
                  {formatCurrency(
                    totalRevenueFromFilteredData,
                    user?.currency || "USD",
                  )}
                </p>
              )}
            </div>
            <div className="flex gap-3 relative">
              {totalRevenueRange === "monthly" && (
                <div className="relative">
                  <select
                    className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8"
                    value={totalMonth}
                    onChange={(e) =>
                      handleTotalMonthChange(Number(e.target.value))
                    }
                  >
                    {recentMonths.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              )}
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8 h-fit"
                  value={totalYear}
                  onChange={(e) =>
                    handleTotalYearChange(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
              <DashboardRangeTabs
                options={[
                  { label: "Monthly", value: "monthly" },
                  { label: "Yearly", value: "yearly" },
                ]}
                value={totalRevenueRange}
                onChange={handleTotalRangeChange}
                shape="full"
                className="border-white/10"
                buttonClassName="px-4 py-1 text-sm"
              />
            </div>
          </div>
          <div className="h-[300px] w-full mt-8">
            {isTotalLoading ? (
              <ChartSkeleton height={300} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={totalRevenueData}>
                  <CartesianGrid
                    vertical={false}
                    stroke="#333"
                    strokeDasharray="2 2"
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  />
                  <Tooltip
                    cursor={{ fill: "rgba(255,255,255,0.05)" }}
                    content={
                      <CustomTooltip currency={user?.currency || "USD"} />
                    }
                  />
                  <Bar
                    dataKey="revenue"
                    fill="#10B981"
                    radius={[2, 2, 0, 0]}
                    barSize={4}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </section>

        <section className="bg-[#1A1A1A]/40 border border-white/5 rounded-xl p-6">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-medium">Revenue Comparison Yearly</h3>
            <div className="flex items-center gap-2">
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-1.5 text-xs text-gray-300 appearance-none pr-7"
                  value={compareYear1}
                  onChange={(e) =>
                    handleCompareYear1Change(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={`left-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={12}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
              <span className="text-xs text-gray-600">Vs</span>
              <div className="relative">
                <select
                  className="bg-[#1A1A1A] border border-white/10 rounded px-3 py-1.5 text-xs text-gray-300 appearance-none pr-7"
                  value={compareYear2}
                  onChange={(e) =>
                    handleCompareYear2Change(Number(e.target.value))
                  }
                >
                  {yearOptions.map((year) => (
                    <option key={`right-${year}`} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={12}
                  className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                />
              </div>
            </div>
          </div>
          <div className="h-[400px] w-full">
            {isYearlyLoading ? (
              <ChartSkeleton height={400} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={yearlyComparisonData}>
                  <defs>
                    {yearlyKeys.map((key, idx) => (
                      <linearGradient
                        key={key}
                        id={`color-${key}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={YEAR_COLORS[idx % YEAR_COLORS.length]}
                          stopOpacity={0.1}
                        />
                        <stop
                          offset="95%"
                          stopColor={YEAR_COLORS[idx % YEAR_COLORS.length]}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid
                    vertical={true}
                    horizontal={false}
                    stroke="#333"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="label"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                    dy={15}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  />
                  <Tooltip
                    content={
                      <CustomTooltip currency={user?.currency || "USD"} />
                    }
                  />
                  {yearlyKeys.map((key, idx) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={YEAR_COLORS[idx % YEAR_COLORS.length]}
                      strokeWidth={3}
                      fill={`url(#color-${key})`}
                      dot={false}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="flex justify-center gap-6 mt-4 text-xs">
            {yearlyKeys.map((key, idx) => (
              <span key={key} className="flex items-center gap-2">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: YEAR_COLORS[idx % YEAR_COLORS.length],
                  }}
                />
                {key.replace("v", "")}
              </span>
            ))}
          </div>
        </section>
      </div>
    </Layout2>
  );
}

export default RevenueReports;
